<template>
    <transition
        :name="transition"
        appear
        @after-enter="afterEnter"
    >
        <div
            ref="wrapper"
            class="modal--layer overflow-auto position-fixed"
            @mousedown="click"
            @touchstart="click"
        >
            <div tabindex="0" />

            <slot />

            <div tabindex="0" />
        </div>
    </transition>
</template>

<script setup>
import { useTemplateRef, onMounted, onBeforeUnmount, nextTick } from "vue"
import { focusFirstFocusable, focusLastFocusable } from "~/lib/utils"
import { useListenerManager } from "front-toolkit/plugins/ListenersPlugin"

const props = defineProps({
    transition: {
        type: String,
        default: "modal-layer",
    },

    handleFocus: Boolean,
    reactToEvents: Boolean,
    skipTrappingFocusWhenThisElementGetsFocus: Element,
})

const emit = defineEmits(["escape", "click-outside"])
const { listen } = useListenerManager()

function getChildren() {
    return Array.from(wrapper.value.children).slice(1, -1)
}

const wrapper = useTemplateRef("wrapper")
let lastFocusedElement = null
let ignoreFocusChange = false

async function afterEnter() {
    if (!props.handleFocus) return
    await nextTick()

    if (getChildren().some(el => el.contains(document.activeElement))) {
        // If the component contains already focused some element, do nothing.
    } else if (props.reactToEvents) {
        focusFirstFocusable({ children: getChildren() })
        lastFocusedElement = document.activeElement
    }

    document.addEventListener("focus", trapFocus, true)
}

function click(event) {
    if (!props.reactToEvents) return

    if (event.target == event.currentTarget) {
        emit("click-outside", event)
    }
}

function trapFocus(event) {
    if (!props.reactToEvents) return
    if (ignoreFocusChange) return

    // Note: this will have no effect if the modal has no focusable elements.
    if (getChildren().some(el => el.contains(event.target))) {
        lastFocusedElement = event.target
    } else {
        // Not part of the main logic of this function.
        // Used only for an ugly corner case.
        // Grep the usages of this prop to get more info.
        // This event handler is run in capture mode, so it runs before almost everything
        // else. This is the only way I could find of conditionaly running the trapping
        // logic.
        const { skipTrappingFocusWhenThisElementGetsFocus: el } = props
        if (el && el.contains(event.target)) return

        focusFirstFocusableSilent({ children: getChildren() })
        if (lastFocusedElement == document.activeElement) {
            focusLastFocusableSilent({
                children: getChildren(),
            })
        }
        lastFocusedElement = document.activeElement
    }
}

function focusFirstFocusableSilent(el) {
    ignoreFocusChange = true
    focusFirstFocusable(el)
    ignoreFocusChange = false
}

function focusLastFocusableSilent(el) {
    ignoreFocusChange = true
    focusLastFocusable(el)
    ignoreFocusChange = false
}

onMounted(() => {
    listen("keyup", event => {
        if (!props.reactToEvents) return

        if (event.key === "Escape") {
            emit("escape", event)
        }
    })
})

onBeforeUnmount(() => {
    document.removeEventListener("focus", trapFocus, true)
})
</script>

<style scoped>
.modal--layer {
    top: -100vh;
    bottom: -100vh;
    left: -100vw;
    right: -100vw;
    padding-top: 100vh;
    padding-bottom: 100vh;
    padding-left: 100vw;
    padding-right: 100vw;
}
</style>
